import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Layout from '@/components/layout/layout'
import {
	User,
	ShoppingBag,
	MapPin,
	Menu,
	X,
	ChevronRight,
	Book,
	LogOut,
} from 'lucide-react'
import {
	AccountFormUpdateData,
	AccountInformation,
	AccountSales,
	AccountDirections,
} from '@/components/steps/steps-account'
import CustomersEntity from '@/entities/api/customers/customer'
import getInitials from '@/utils/getInitials'
import { API_URL } from '@/services/api/keys'
import AuthRequiredScreen from '@/components/default/auth-required'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/store/store'
import { logout } from '@/store/features/auth'
import { clearCart } from '@/store/features/cart'

interface MenuItem {
	id: string
	label: string
	icon: React.ReactNode
	component: React.ReactNode
}

const Account = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	const { zone } = useParams<{ zone: string }>()
	const navigate = useNavigate()
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false)
	const dispatch = useDispatch<AppDispatch>()

	const zoneMapping: { [key: string]: string } = {
		informacion: 'information',
		compras: 'purchases',
		direcciones: 'addresses',
		configuracion: 'settings',
	}

	const reverseZoneMapping: { [key: string]: string } = {
		information: 'informacion',
		purchases: 'compras',
		addresses: 'direcciones',
		settings: 'configuracion',
	}

	const validZones = Object.keys(zoneMapping)
	const activeSection =
		zone && validZones.includes(zone) ? zoneMapping[zone] : 'information'

	useEffect(() => {
		if (!zone || !validZones.includes(zone)) {
			navigate('/Perfil/informacion', { replace: true })
		}
	}, [zone, navigate])

	const menuItems: MenuItem[] = [
		{
			id: 'information',
			label: 'Información Personal',
			icon: <User className='w-4 h-4' />,
			component: <AccountInformation customer={customer} token={token} />,
		},
		{
			id: 'purchases',
			label: 'Historial de Compras',
			icon: <ShoppingBag className='w-4 h-4' />,
			component: <AccountSales customer={customer} token={token} />,
		},
		{
			id: 'addresses',
			label: 'Mis Direcciones',
			icon: <MapPin className='w-4 h-4' />,
			component: <AccountDirections customer={customer} token={token} />,
		},
		{
			id: 'settings',
			label: 'Datos',
			icon: <Book className='w-4 h-4' />,
			component: <AccountFormUpdateData customer={customer} token={token} />,
		},
	]

	const getCurrentComponent = () => {
		const currentItem = menuItems.find((item) => item.id === activeSection)
		return (
			currentItem?.component || (
				<AccountInformation customer={customer} token={token} />
			)
		)
	}

	const getCurrentLabel = () => {
		const currentItem = menuItems.find((item) => item.id === activeSection)
		return currentItem?.label || 'Información Personal'
	}

	const handleSectionChange = (sectionId: string) => {
		navigate(`/Perfil/${reverseZoneMapping[sectionId]}`)
		setIsMobileMenuOpen(false)
	}

	const handleLogout = () => {
		dispatch(logout())
		dispatch(clearCart())
	}

	if (!customer || !token) {
		return (
			<Layout customer={null} token={null}>
				<AuthRequiredScreen />
			</Layout>
		)
	}

	return (
		<Layout customer={customer} token={token}>
			<div className='min-h-screen bg-gradient-to-b from-gray-50 via-gray-50 to-white'>
				<div className='lg:hidden bg-white/95 backdrop-blur-md border-b border-gray-100 sticky top-0 z-40 shadow-sm'>
					<div className='px-3 py-3 sm:px-4 sm:py-4'>
						<div className='flex items-center justify-between'>
							<div className='flex items-center space-x-3'>
								{customer && (
									<>
										{customer.images && customer.images.length > 0 ? (
											<img
												src={`${API_URL}/${customer.images[0].url}`}
												alt='Profile'
												className='w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover ring-2 ring-red-500/50'
											/>
										) : (
											<div className='w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center ring-2 ring-red-500/50'>
												<span className='text-white font-semibold text-xs sm:text-sm'>
													{getInitials(customer.name, customer.lastname)}
												</span>
											</div>
										)}
										<div className='min-w-0 flex-1'>
											<h1 className='text-sm sm:text-base font-bold text-gray-900 truncate'>
												{getCurrentLabel()}
											</h1>
											<p className='text-xs sm:text-sm text-gray-500 truncate'>
												{customer.name} {customer.lastname}
											</p>
										</div>
									</>
								)}
							</div>
							<button
								onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
								className='p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors'
							>
								{isMobileMenuOpen ? (
									<X className='w-4 h-4 sm:w-5 sm:h-5' />
								) : (
									<Menu className='w-4 h-4 sm:w-5 sm:h-5' />
								)}
							</button>
						</div>
					</div>
				</div>

				<div className='w-full max-w-none px-3 sm:px-4 lg:px-6 xl:px-8 py-3 sm:py-4 lg:py-6 lg:container lg:mx-auto'>
					<div className='flex flex-col lg:grid lg:grid-cols-12 gap-4 lg:gap-6 xl:gap-8 w-full'>
						<aside className='hidden lg:block lg:col-span-4 xl:col-span-3'>
							<div className='lg:sticky lg:top-6'>
								<div className='bg-white rounded-2xl shadow-sm border border-gray-200/60 overflow-hidden'>
									<div className='relative p-4 sm:p-6 bg-gradient-to-br from-gray-900 via-gray-800 to-black'>
										<div className='absolute inset-0 bg-gradient-to-r from-red-600/10 to-transparent'></div>
										<div className='relative'>
											<div>
												<h1 className='text-lg xl:text-xl font-bold text-white mb-4'>
													Mi Cuenta
												</h1>
												{customer && (
													<div className='flex items-center space-x-3'>
														{customer.images && customer.images.length > 0 ? (
															<img
																src={`${API_URL}/${customer.images[0].url}`}
																alt='Profile'
																className='w-12 h-12 xl:w-14 xl:h-14 rounded-xl object-cover ring-3 ring-white/20'
															/>
														) : (
															<div className='w-12 h-12 xl:w-14 xl:h-14 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center ring-3 ring-white/20'>
																<span className='text-white font-bold text-sm xl:text-base'>
																	{getInitials(
																		customer.name,
																		customer.lastname
																	)}
																</span>
															</div>
														)}
														<div className='flex-1 min-w-0'>
															<p className='text-white font-semibold text-sm xl:text-base truncate'>
																{customer.name} {customer.lastname}
															</p>
															<p className='text-gray-300 text-xs xl:text-sm truncate'>
																{customer.email}
															</p>
															<div className='flex items-center mt-2'>
																<div className='w-1.5 h-1.5 bg-green-400 rounded-full mr-1.5'></div>
																<span className='text-green-300 text-xs font-medium'>
																	En línea
																</span>
															</div>
														</div>
													</div>
												)}
											</div>
										</div>
									</div>

									<nav className='bg-white'>
										<div className='p-2 sm:p-3'>
											{menuItems.map((item, index) => (
												<button
													key={item.id}
													onClick={() => handleSectionChange(item.id)}
													className={`w-full flex items-center justify-between px-3 py-3 sm:px-4 sm:py-3.5 rounded-xl transition-all duration-200 group ${
														index < menuItems.length - 1 ? 'mb-1' : ''
													} ${
														activeSection === item.id
															? 'bg-red-50 text-red-700 ring-1 ring-red-200'
															: 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
													}`}
												>
													<div className='flex items-center space-x-3'>
														<div
															className={`flex-shrink-0 p-1.5 sm:p-2 rounded-lg ${
																activeSection === item.id
																	? 'bg-red-500 text-white'
																	: 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
															} transition-colors duration-200`}
														>
															{item.icon}
														</div>
														<span className='font-medium text-sm sm:text-base truncate'>
															{item.label}
														</span>
													</div>
													<ChevronRight
														className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-200 flex-shrink-0 ${
															activeSection === item.id
																? 'rotate-90 text-red-600'
																: 'text-gray-400 group-hover:text-gray-600'
														}`}
													/>
												</button>
											))}
											<button
												onClick={handleLogout}
												className='w-full cursor-pointer mt-4 flex items-center justify-between px-3 py-3 sm:px-4 sm:py-3.5 rounded-xl transition-all duration-200 group bg-white text-red-700 ring-1 ring-red-200 hover:bg-red-100 hover:text-red-800'
											>
												<div className='flex items-center space-x-3'>
													<div className='flex-shrink-0 p-1.5 sm:p-2 rounded-lg bg-red-500 text-white transition-colors duration-200 group-hover:bg-red-600'>
														<LogOut className='w-4 h-4 sm:w-5 sm:h-5' />
													</div>
													<span className='font-medium text-sm sm:text-base truncate'>
														Cerrar sesión
													</span>
												</div>
											</button>
										</div>
									</nav>
								</div>
							</div>
						</aside>

						<main className='w-full lg:col-span-8 xl:col-span-9'>
							<div className='bg-white rounded-2xl shadow-sm border border-gray-200/60 overflow-hidden w-full'>
								<div className='border-b border-gray-100 px-4 py-4 sm:px-6 sm:py-6 bg-gradient-to-r from-gray-50/50 to-white'>
									<div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4'>
										<div className='min-w-0 flex-1'>
											<h2 className='text-xl sm:text-2xl font-bold text-gray-900 mb-1'>
												{getCurrentLabel()}
											</h2>
											<p className='text-gray-600 text-sm'>
												Gestiona tu información de cuenta y preferencias
											</p>
										</div>
										<div className='flex items-center space-x-2 flex-shrink-0'>
											<div className='flex items-center space-x-1.5 bg-green-50 px-2 py-1 rounded-full'>
												<div className='w-1.5 h-1.5 bg-green-500 rounded-full'></div>
												<span className='text-xs font-medium text-green-700'>
													En línea
												</span>
											</div>
											<div className='text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full font-medium'>
												SportCenter Shop
											</div>
										</div>
									</div>
								</div>

								<div className='p-4 sm:p-6 w-full'>
									<div className='w-full max-w-full'>
										{getCurrentComponent()}
									</div>
								</div>
							</div>
						</main>

						{isMobileMenuOpen && (
							<div className='lg:hidden fixed inset-x-3 top-20 z-50 bg-white rounded-2xl shadow-xl border border-gray-200 max-h-[calc(100vh-100px)] overflow-hidden'>
								<nav className='p-2 overflow-y-auto max-h-full'>
									{menuItems.map((item, index) => (
										<button
											key={item.id}
											onClick={() => handleSectionChange(item.id)}
											className={`w-full flex items-center justify-between px-3 py-3.5 rounded-xl transition-all duration-200 group ${
												index < menuItems.length - 1 ? 'mb-1' : ''
											} ${
												activeSection === item.id
													? 'bg-red-50 text-red-700 ring-1 ring-red-200'
													: 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
											}`}
										>
											<div className='flex items-center space-x-3'>
												<div
													className={`flex-shrink-0 p-2 rounded-lg ${
														activeSection === item.id
															? 'bg-red-500 text-white'
															: 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
													} transition-colors duration-200`}
												>
													{item.icon}
												</div>
												<span className='font-medium text-base truncate'>
													{item.label}
												</span>
											</div>
											<ChevronRight
												className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${
													activeSection === item.id
														? 'rotate-90 text-red-600'
														: 'text-gray-400 group-hover:text-gray-600'
												}`}
											/>
										</button>
									))}
								</nav>
							</div>
						)}
					</div>
				</div>

				{isMobileMenuOpen && (
					<>
						<div
							className='fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden'
							onClick={() => setIsMobileMenuOpen(false)}
						></div>
					</>
				)}
			</div>
		</Layout>
	)
}

export default Account
